import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import { Line } from 'react-chartjs-2'
import { ChartOptions, DecimationAlgorithm, Tick } from 'chart.js'
import 'chartjs-adapter-date-fns'

import dayjs from 'dayjs'
import { addUnitSuffix } from '../../../utils/dataFormat'
import { MetricTxType } from '../../../api/responseType'

type Props = {
  mainChartData: MetricTxType['metric']
}

export default function MainLineChart({ mainChartData }: Props) {
  const [dates, setDates] = useState<string[]>([])
  const [counts, setCounts] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const makeData = (dates: string[], counts: string[]) => {
    return {
      labels: dates, // date 배열
      datasets: [
        {
          label: 'Txn',
          data: counts,
          fill: false,
          borderColor: '#2cc3d5',
          pointRadius: 0,
        },
      ],
    }
  }
  const setOption = (dates: string[], counts: string[]): ChartOptions<'line'> => {
    const dataSets = makeData(dates, counts).datasets
    return {
      // NOTE: Animation Option
      animations: {
        tension: {
          duration: 0,
        },
      },
      // NOTE: hover 시 뜨는 tooltip 위치
      interaction: {
        mode: 'nearest',
        intersect: false,
      },
      plugins: {
        // NOTE: 범례 보이지 않게 설정
        legend: {
          display: false,
        },

        decimation: {
          enabled: true,
          algorithm: 'lttb' as DecimationAlgorithm.lttb,
          samples: 100,
        },

        tooltip: {
          displayColors: false,
          backgroundColor: '#efefef',
          titleColor: '#000',
          bodyColor: '#000',
          padding: 10,
          cornerRadius: 0,

          callbacks: {
            title(tooltipItem) {
              const title = tooltipItem[0].label
              return dayjs(title.slice(0, title.lastIndexOf(',')))
                .format('ddd, D, M, YYYY')
                .toString()
            },
            label(tooltipItem) {
              return dataSets.map((ds) => {
                const label = `${ds.label}: `
                return label + new Intl.NumberFormat('en-US').format(Number(ds.data[tooltipItem.dataIndex]))
              })
            },
          },
        },
      },

      scales: {
        x: {
          type: 'time',
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            autoSkipPadding: 50,
          },
        },
        y: {
          grid: {
            display: false,
          },
          beginAtZero: counts.includes('0'),
          ticks: {
            callback: (tickValue: number | string, index: number, ticks: Tick[]): string => {
              if (index !== 0 && index !== ticks.length - 1 && ticks[index].value - ticks[index - 1].value < 1) {
                return ''
              }
              return addUnitSuffix(parseInt(tickValue as string, 10), 'number')
            },
          },
          position: 'right',
        },
      },
      responsive: true,
    }
  }

  useEffect(() => {
    if (mainChartData) {
      const dateList = mainChartData.map(({ date }) => date)
      const countList = mainChartData.map(({ count }) => (count ? count.toString() : ''))

      setDates(dateList)
      setCounts(countList)
      setLoading(false)
    }
  }, [mainChartData])

  return (
    <MainWrap>
      {!loading && <Line height={115} data={makeData(dates, counts)} options={setOption(dates, counts)} />}
    </MainWrap>
  )
}

const MainWrap = styled.div`
  width: 100%;
  height: 100%;
  padding-bottom: 2rem;
`
